import { useEffect, useMemo } from "react";
import { useLocation, Link } from "wouter";
import { useAuth, logout } from "@/hooks/use-auth";
import { useListEngineerJobs, getListEngineerJobsQueryKey } from "@workspace/api-client-react";
import { useInspections } from "@/hooks/use-inspections";
import { Wrench, LogOut, Loader2, AlertTriangle, ClipboardList, Map, ChevronRight } from "lucide-react";
import logo from "/images/brand/vertex-logo.png";

export default function EngineerPortal() {
  const { data: user, isLoading: isAuthLoading } = useAuth();
  const [, setLocation] = useLocation();
  
  // Use existing hooks
  const { data: jobs, isLoading: isJobsLoading, isError: isJobsError } = useListEngineerJobs({
    query: {
      enabled: !!user && user.role === "engineer",
      queryKey: getListEngineerJobsQueryKey(),
      refetchInterval: 15000,
      staleTime: 0
    }
  });
  
  const { data: allInspections, isLoading: isInspectionsLoading, isError: isInspectionsError } = useInspections();

  useEffect(() => {
    if (!isAuthLoading) {
      if (!user) {
        setLocation("/admin"); 
      } else if (user.role === "admin") {
        setLocation("/admin/dashboard");
      }
    }
  }, [user, isAuthLoading, setLocation]);

  const stats = useMemo(() => {
    const callouts = jobs?.filter(j => j.kind === "callout" && j.status !== "cancelled" && j.status !== "submitted") || [];
    const siteSurveys = jobs?.filter(j => j.kind === "site_survey" && j.status !== "cancelled" && j.status !== "submitted") || [];
    const ppms = allInspections?.filter(i => i.engineerId === user?.id && i.status !== "completed") || [];
    
    return {
      callouts: callouts.length,
      siteSurveys: siteSurveys.length,
      ppms: ppms.length
    };
  }, [jobs, allInspections, user?.id]);

  if (isAuthLoading || !user) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (isJobsError || isInspectionsError) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4 text-center">
        <AlertTriangle className="w-12 h-12 text-red-500 mb-4" />
        <h2 className="text-xl font-bold text-slate-900 mb-2">Error Loading Dashboard</h2>
        <p className="text-slate-600 mb-6">There was a problem connecting to the server.</p>
        <button onClick={() => window.location.reload()} className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors">Retry</button>
      </div>
    );
  }

  const isLoading = isJobsLoading || isInspectionsLoading;

  return (
    <div className="min-h-[100dvh] bg-slate-50 flex flex-col">
      <header className="bg-slate-900 border-b border-slate-800 sticky top-0 z-30 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={logo} alt="VertexAir" className="h-7 brightness-0 invert" />
            <div className="h-6 w-px bg-slate-700 mx-1"></div>
            <div className="flex items-center gap-2 text-white">
              <Wrench className="w-4 h-4 text-primary" />
              <span className="font-semibold text-sm">Engineer Portal</span>
            </div>
          </div>
          <button onClick={logout} className="p-2 -mr-2 text-slate-400 hover:text-white transition-colors" title="Logout">
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </header>

      <main className="flex-1 max-w-4xl w-full mx-auto p-4 space-y-6">
        <div className="mt-2 mb-8">
          <h1 className="text-2xl font-bold text-slate-900">Welcome, {user.name.split(' ')[0]}</h1>
          <p className="text-sm text-slate-500 mt-1">Select a module to view your assigned work.</p>
        </div>

        {isLoading ? (
          <div className="py-12 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-slate-300" /></div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link href="/engineer/callouts" className="bg-white border border-slate-200 rounded-2xl p-6 flex flex-col items-center text-center gap-4 hover:border-primary/50 hover:shadow-md transition-all group">
              <div className="w-16 h-16 rounded-full bg-rose-50 text-rose-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                <AlertTriangle className="w-8 h-8" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900 mb-1">Call Outs</h3>
                <p className="text-sm text-slate-500">Breakdowns and urgent repairs</p>
              </div>
              <div className="mt-auto pt-4 w-full flex items-center justify-between border-t border-slate-100">
                <span className="text-sm font-semibold text-rose-600">{stats.callouts} Active</span>
                <ChevronRight className="w-4 h-4 text-slate-400" />
              </div>
            </Link>

            <Link href="/engineer/site-surveys" className="bg-white border border-slate-200 rounded-2xl p-6 flex flex-col items-center text-center gap-4 hover:border-primary/50 hover:shadow-md transition-all group">
              <div className="w-16 h-16 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                <Map className="w-8 h-8" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900 mb-1">Site Survey</h3>
                <p className="text-sm text-slate-500">Assessments and quotes</p>
              </div>
              <div className="mt-auto pt-4 w-full flex items-center justify-between border-t border-slate-100">
                <span className="text-sm font-semibold text-blue-600">{stats.siteSurveys} Active</span>
                <ChevronRight className="w-4 h-4 text-slate-400" />
              </div>
            </Link>

            <Link href="/engineer/ppm" className="bg-white border border-slate-200 rounded-2xl p-6 flex flex-col items-center text-center gap-4 hover:border-primary/50 hover:shadow-md transition-all group">
              <div className="w-16 h-16 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                <ClipboardList className="w-8 h-8" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900 mb-1">PPM</h3>
                <p className="text-sm text-slate-500">Planned maintenance</p>
              </div>
              <div className="mt-auto pt-4 w-full flex items-center justify-between border-t border-slate-100">
                <span className="text-sm font-semibold text-emerald-600">{stats.ppms} Drafts</span>
                <ChevronRight className="w-4 h-4 text-slate-400" />
              </div>
            </Link>
          </div>
        )}
      </main>
    </div>
  );
}
